import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import QRCode from "qrcode";
import API, { API_BASE_URL } from "../services/api";
import { formatDateTimeDMY } from "../utils/dateFormat";
import SupportVideoCall from "./SupportVideoCall";

const CATEGORY_LABELS = {
  sugerencias: "Sugerencias",
  felicitaciones: "Felicitaciones",
  reclamos: "Reclamos",
  devoluciones: "Devoluciones"
};

const STATUS_LABELS = {
  open: "Abierto",
  in_progress: "En curso",
  resolved: "Resuelto",
  closed: "Cerrado"
};

const EV_STATUS_LABELS = {
  queued: "En cola",
  dispatched: "Enviado",
  completed: "Completado",
  failed: "Falló",
  cancelled: "Cancelado"
};

function publicMenuUrl(machineId) {
  return `${window.location.origin}/soporte/${machineId}`;
}

function MachineQrImage({ url, size = 220 }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, {
      width: size * 2,
      margin: 2,
      errorCorrectionLevel: "M"
    })
      .then(dataUrl => {
        if (!cancelled) setSrc(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setSrc("");
      });
    return () => {
      cancelled = true;
    };
  }, [url, size]);

  if (!src) {
    return (
      <div className="qr-machine-card__placeholder" style={{ width: size, height: size }}>
        Generando QR…
      </div>
    );
  }
  return (
    <img
      className="qr-machine-card__img"
      src={src}
      width={size}
      height={size}
      alt={`Código QR ${url}`}
    />
  );
}

function printQrSticker(machine, dataUrl) {
  const w = window.open("", "_blank", "noopener,width=520,height=720");
  if (!w) return;
  const name = machine.name || `Máquina ${machine.id}`;
  w.document.write(`<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <title>QR soporte · ${name}</title>
  <style>
    body { font-family: system-ui, sans-serif; padding: 24px; text-align: center; }
    .sticker { width: 90mm; margin: 0 auto; border: 2px dashed #333; border-radius: 8px; padding: 12px; }
    img { width: 55mm; height: 55mm; }
    h1 { font-size: 16px; margin: 0 0 6px; }
    p { font-size: 12px; margin: 4px 0; color: #333; }
    @media print { .no-print { display: none; } body { padding: 0; } }
  </style>
</head>
<body>
  <p class="no-print">Imprimí este sticker y pegalo en la máquina.</p>
  <div class="sticker">
    <h1>¿Necesitás ayuda?</h1>
    <p>Escaneá el código con tu celular</p>
    <img src="${dataUrl}" alt="QR" />
    <p><strong>Vikingo Systems</strong></p>
    <p>Máquina ${machine.id}</p>
    <p>Sugerencias · Felicitaciones · Reclamos · Devoluciones</p>
    <p>Si hay videollamada de soporte: es en vivo y no se graba.</p>
  </div>
</body>
</html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
}

function QrMachineCard({ machine }) {
  const url = publicMenuUrl(machine.id);
  const [png, setPng] = useState("");
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, { width: 1024, margin: 2, errorCorrectionLevel: "M" }).then(d => {
      if (!cancelled) setPng(d);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <article className="qr-machine-card">
      <h3 className="qr-machine-card__title">{machine.name}</h3>
      <p className="qr-machine-card__meta">
        id {machine.id}
        {machine.location ? ` · ${machine.location}` : ""}
      </p>
      <div className="qr-machine-card__code">
        <MachineQrImage url={url} size={220} />
      </div>
      <p className="qr-machine-card__hint">Escaneá este código con el celular del cliente</p>
      <code className="qr-machine-card__url">{url}</code>
      <div className="qr-machine-card__actions">
        <a className="btn btn-primary" href={url} target="_blank" rel="noreferrer">
          Ver menú
        </a>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => png && printQrSticker(machine, png)}
          disabled={!png}
        >
          Imprimir sticker
        </button>
      </div>
    </article>
  );
}

function TicketContextPanel({ ticket, busy, onRefresh }) {
  const [ctx, setCtx] = useState(null);
  const [loadingCtx, setLoadingCtx] = useState(false);
  const [slotId, setSlotId] = useState("");
  const [err, setErr] = useState("");
  const [operatorMic, setOperatorMic] = useState(null);

  const loadCtx = useCallback(async () => {
    setLoadingCtx(true);
    setErr("");
    try {
      const res = await API.get(`/support/tickets/${ticket.id}/context`);
      setCtx(res.data);
      if (res.data?.slots?.length) {
        setSlotId(prev => prev || String(res.data.slots[0].id));
      }
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || "Error al cargar contexto");
    } finally {
      setLoadingCtx(false);
    }
  }, [ticket.id]);

  useEffect(() => {
    setSlotId("");
    setCtx(null);
    loadCtx();
  }, [ticket.id, loadCtx]);

  useEffect(() => {
    if (!ctx?.remote_sessions?.some(s => ["requested", "active"].includes(s.status))) {
      return undefined;
    }
    const timer = setInterval(loadCtx, 4000);
    return () => clearInterval(timer);
  }, [ctx, loadCtx]);

  const startRemote = async () => {
    if (
      !window.confirm(
        "¿Iniciar videollamada EN VIVO con el celular de la máquina? El sistema no graba ni guarda imagen ni voz. SumUp no se usa."
      )
    ) {
      return;
    }
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false
          });
          setOperatorMic(stream);
        } catch (_) {
          alert(
            "Safari/Chrome necesita el micrófono para que te oigan. Permitilo y reintentá si no hay audio."
          );
        }
      }
      await API.post(`/support/tickets/${ticket.id}/remote-session`);
      await loadCtx();
      onRefresh();
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo iniciar sesión remota");
    }
  };

  const stopRemote = async () => {
    try {
      if (operatorMic) {
        operatorMic.getTracks().forEach(t => t.stop());
        setOperatorMic(null);
      }
      await API.post(`/support/tickets/${ticket.id}/remote-session/stop`);
      await loadCtx();
      onRefresh();
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo cerrar sesión");
    }
  };

  const emergencyVend = async () => {
    if (!slotId) {
      alert("Elegí una buchaca");
      return;
    }
    const slot = ctx?.slots?.find(s => String(s.id) === String(slotId));
    const label = slot
      ? `${slot.slot_code} · ${slot.product_name || "producto"}`
      : slotId;
    if (
      !window.confirm(
        `¿Autorizar entrega de emergencia en buchaca ${label}? Se audita en logs y no pasa por SumUp.`
      )
    ) {
      return;
    }
    try {
      await API.post(`/support/tickets/${ticket.id}/emergency-vend`, {
        slot_id: Number(slotId),
        reason: `Operador autorizó entrega emergencia · ticket #${ticket.id}`
      });
      await loadCtx();
      onRefresh();
      alert("Comando enviado al terminal. Revisá el estado en unos segundos.");
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo encolar entrega");
    }
  };

  const ticketOpen = ticket.status === "open" || ticket.status === "in_progress";
  const canCall = ticketOpen && Boolean(ticket.machine_id);
  const canEmergency = ticket.category === "devoluciones" && ticketOpen;
  const cameraConsent = Boolean(ctx?.camera_live_consent);
  const canStartCall = canCall && cameraConsent;
  const activeSession = ctx?.remote_sessions?.find(s =>
    ["requested", "active"].includes(s.status)
  );

  return (
    <div className={`support-ticket-stage${activeSession ? " is-on-call" : ""}`}>
      <div className="support-ticket-ops">
        {loadingCtx && !ctx ? (
          <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>Cargando logs…</p>
        ) : null}
        {err ? <p className="form-error">{err}</p> : null}
        {ctx ? (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", marginBottom: "0.5rem" }}>
              <span
                className={`badge ${ctx.payment_idle ? "badge--success" : "badge--danger"}`}
                title="SumUp no debe estar cobrando para cámara o entrega remota"
              >
                SumUp: {ctx.payment_idle ? "idle" : "cobro activo"}
              </span>
              {ctx.device ? (
                <span className="badge badge--neutral">Terminal #{ctx.device.id}</span>
              ) : (
                <span className="badge badge--warning">Sin terminal activo</span>
              )}
              {activeSession ? (
                <span className="badge badge--accent">Videollamada activa</span>
              ) : null}
              {cameraConsent ? (
                <span className="badge badge--success">Cliente autorizó video en vivo</span>
              ) : (
                <span className="badge badge--warning">Sin autorización de cámara</span>
              )}
            </div>

            {canCall || canEmergency ? (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                  alignItems: "flex-end",
                  marginBottom: "0.65rem"
                }}
              >
              {canCall ? (
                !activeSession ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy || !ctx.payment_idle || !ctx.device || !canStartCall}
                    onClick={startRemote}
                    title={
                      canStartCall
                        ? "Transmisión en vivo; el sistema no graba ni guarda imagen"
                        : "El cliente no autorizó videollamada. Atendé sin cámara."
                    }
                  >
                    Iniciar videollamada
                  </button>
                ) : (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={busy}
                      onClick={stopRemote}
                    >
                      Colgar videollamada
                    </button>
                  )
                ) : null}
                {canEmergency ? (
                  <>
                    <label style={{ fontSize: "0.85rem" }}>
                      Buchaca{" "}
                      <select
                        className="input"
                        value={slotId}
                        onChange={e => setSlotId(e.target.value)}
                        style={{ minWidth: "160px" }}
                      >
                        {(ctx.slots || []).map(s => (
                          <option key={s.id} value={s.id}>
                            {s.slot_code} · {s.product_name || "—"} (stock {s.stock})
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={busy || !ctx.payment_idle || !ctx.device || !slotId}
                      onClick={emergencyVend}
                    >
                      Entrega de emergencia
                    </button>
                  </>
                ) : null}
              </div>
            ) : null}
            {canCall && !cameraConsent ? (
              <p style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", margin: "0 0 0.65rem" }}>
                Sin autorización de cámara no se puede iniciar video. Pedile al cliente
                que entre al QR de esta máquina, ponga el número del ticket y toque
                “Autorizar videollamada en vivo”. Recargá este panel después. Atendé
                por logs, WhatsApp o entrega de emergencia si no quiere cámara.
              </p>
            ) : null}

            {ctx.recent_events?.length ? (
              <details style={{ fontSize: "0.82rem", marginBottom: "0.5rem" }}>
                <summary>Logs recientes de la máquina ({ctx.recent_events.length})</summary>
                <ul style={{ margin: "0.35rem 0 0", paddingLeft: "1.1rem" }}>
                  {ctx.recent_events.map(ev => (
                    <li key={ev.id}>
                      <code>{ev.event_type}</code>
                      {ev.reason ? ` · ${ev.reason}` : ""}
                      {" · "}
                      {formatDateTimeDMY(ev.created_at)}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}

            {ctx.emergency_vends?.length ? (
              <div style={{ fontSize: "0.82rem" }}>
                <strong>Entregas emergencia:</strong>
                <ul style={{ margin: "0.25rem 0 0", paddingLeft: "1.1rem" }}>
                  {ctx.emergency_vends.map(ev => (
                    <li key={ev.id}>
                      #{ev.id} · {ev.slot_code || ev.slot_id} ·{" "}
                      {EV_STATUS_LABELS[ev.status] || ev.status}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
      {activeSession ? (
        <SupportVideoCall
          key={activeSession.id}
          machineId={ticket.machine_id}
          ticketId={ticket.id}
          initialAudioStream={operatorMic}
          active
        />
      ) : null}
    </div>
  );
}

function WhatsAppSettings() {
  const [value, setValue] = useState("+56979674448");
  const [saved, setSaved] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get("/support/whatsapp");
        if (!cancelled) {
          const v = res.data?.support_whatsapp || "+56979674448";
          setValue(v);
          setSaved(v);
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e?.response?.data?.error || "No se pudo cargar el WhatsApp");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      const res = await API.put("/support/whatsapp", { support_whatsapp: value });
      const v = res.data?.support_whatsapp || value;
      setValue(v);
      setSaved(v);
    } catch (e2) {
      setErr(e2?.response?.data?.error || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card card--padded" style={{ marginBottom: "1.25rem" }}>
      <h2 className="section-title" style={{ marginTop: 0 }}>
        WhatsApp de atención
      </h2>
      <p style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", marginBottom: "0.85rem" }}>
        Los clientes no te dejan su número: al tocar &quot;Continuar por WhatsApp&quot; le
        escriben a <strong>este</strong> número, con el ticket ya armado. Podés cambiarlo cuando
        quieras.
      </p>
      <form
        onSubmit={save}
        style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "flex-end" }}
      >
        <label style={{ fontSize: "0.85rem" }}>
          Número (con código de país)
          <input
            className="input"
            type="tel"
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder="+56979674448"
            disabled={loading || saving}
            style={{ display: "block", minWidth: "220px", marginTop: "0.25rem" }}
          />
        </label>
        <button type="submit" className="btn btn-primary" disabled={loading || saving}>
          {saving ? "Guardando…" : "Guardar"}
        </button>
      </form>
      {err ? <p className="form-error">{err}</p> : null}
      {saved && !err ? (
        <p style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", marginTop: "0.5rem" }}>
          Activo: {saved}
        </p>
      ) : null}
    </section>
  );
}

export default function SupportAdmin() {
  const [machines, setMachines] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [emergencyVends, setEmergencyVends] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active");
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [machinesRes, ticketsRes, evRes] = await Promise.all([
        API.get("/machines"),
        API.get("/support/tickets", { params: { status: statusFilter } }),
        API.get("/support/emergency-vends").catch(() => ({ data: [] }))
      ]);
      setMachines(Array.isArray(machinesRes.data) ? machinesRes.data : []);
      setTickets(Array.isArray(ticketsRes.data) ? ticketsRes.data : []);
      setEmergencyVends(Array.isArray(evRes.data) ? evRes.data : []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const updateTicket = async (id, status) => {
    setBusyId(id);
    try {
      await API.patch(`/support/tickets/${id}`, { status });
      await load();
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo actualizar");
    } finally {
      setBusyId(null);
    }
  };

  const openCount = tickets.filter(t => t.status === "open").length;
  const refundOpen = tickets.filter(
    t => t.category === "devoluciones" && t.status === "open"
  ).length;

  return (
    <div>
      <header className="page-header">
        <h1 className="page-title">Soporte al cliente (QR)</h1>
        <p className="page-subtitle">
          Menú público por máquina, tickets WhatsApp, revisión de logs, asistencia remota (fase 2)
          y entrega de emergencia auditada cuando SumUp está idle.
        </p>
      </header>

      <div className="home-quick-grid" style={{ marginBottom: "1.25rem" }}>
        <Link to="/control" className="home-quick-card">
          <strong>Control operativo</strong>
          <span>Semáforo USB / MDB y alertas en vivo</span>
        </Link>
        <a
          href={`${API_BASE_URL}/health`}
          target="_blank"
          rel="noreferrer"
          className="home-quick-card"
        >
          <strong>API Vikingo</strong>
          <span>Estado del backend ({API_BASE_URL})</span>
        </a>
        {machines[0] ? (
          <a
            href={publicMenuUrl(machines[0].id)}
            target="_blank"
            rel="noreferrer"
            className="home-quick-card home-quick-card--accent"
          >
            <strong>Vista cliente (demo)</strong>
            <span>Abrir menú QR · {machines[0].name}</span>
          </a>
        ) : null}
      </div>

      <WhatsAppSettings />

      <details className="card card--padded" style={{ marginBottom: "1.25rem" }}>
        <summary className="section-title" style={{ marginTop: 0, cursor: "pointer" }}>
          QR por máquina (para escanear)
        </summary>
        <p style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", margin: "0.75rem 0 1rem" }}>
          El cliente apunta la cámara de <strong>su</strong> celular a este código. Pegá el
          autoadhesivo en la máquina.
        </p>
        {loading && !machines.length ? (
          <p style={{ color: "var(--color-text-muted)" }}>Cargando máquinas…</p>
        ) : !machines.length ? (
          <p style={{ color: "var(--color-text-muted)" }}>
            No hay máquinas. Creá una en <Link to="/machines">Máquinas</Link>.
          </p>
        ) : (
          <div className="qr-machine-grid">
            {machines.map(m => (
              <QrMachineCard key={m.id} machine={m} />
            ))}
          </div>
        )}
      </details>

      {emergencyVends.length > 0 ? (
        <section className="card card--padded" style={{ marginBottom: "1.25rem" }}>
          <h2 className="section-title" style={{ marginTop: 0 }}>
            Historial entregas de emergencia
          </h2>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Id</th>
                  <th>Ticket</th>
                  <th>Máquina</th>
                  <th>Buchaca</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {emergencyVends.slice(0, 15).map(ev => (
                  <tr key={ev.id}>
                    <td>#{ev.id}</td>
                    <td>{ev.ticket_id ? `#${ev.ticket_id}` : "—"}</td>
                    <td>{ev.machine_name || ev.machine_id}</td>
                    <td>
                      {ev.slot_code || ev.slot_id}
                      {ev.product_name ? ` · ${ev.product_name}` : ""}
                    </td>
                    <td>{EV_STATUS_LABELS[ev.status] || ev.status}</td>
                    <td>{formatDateTimeDMY(ev.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="card card--padded">
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            gap: "0.75rem",
            marginBottom: "1rem"
          }}
        >
          <h2 className="section-title" style={{ margin: 0 }}>
            Tickets
            {(statusFilter === "open" || statusFilter === "active") && openCount > 0 ? (
              <span className="badge badge--danger" style={{ marginLeft: "0.5rem" }}>
                {openCount} abiertos
              </span>
            ) : null}
            {refundOpen > 0 ? (
              <span className="badge badge--warning" style={{ marginLeft: "0.35rem" }}>
                {refundOpen} devoluciones
              </span>
            ) : null}
          </h2>
          <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
            {["active", "open", "in_progress", "resolved", "all"].map(s => (
              <button
                key={s}
                type="button"
                className={`btn ${statusFilter === s ? "btn-primary" : "btn-secondary"}`}
                onClick={() => {
                  setLoading(true);
                  setStatusFilter(s);
                }}
              >
                {s === "all" ? "Todos" : s === "active" ? "Pendientes" : STATUS_LABELS[s] || s}
              </button>
            ))}
          </div>
        </div>

        {error ? <p className="form-error">{error}</p> : null}

        {!tickets.length ? (
          <p style={{ color: "var(--color-text-muted)" }}>Sin tickets en este filtro.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
            {tickets.map(t => (
              <div
                key={t.id}
                className="support-ticket-row"
                data-category={t.category}
                data-status={t.status}
              >
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    gap: "0.5rem"
                  }}
                >
                  <strong>
                    #{t.id} · {CATEGORY_LABELS[t.category] || t.category}
                    {t.machine_name ? ` · ${t.machine_name}` : ""}
                  </strong>
                  <span style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                    {STATUS_LABELS[t.status] || t.status} · {formatDateTimeDMY(t.created_at)}
                  </span>
                </div>
                {t.missing_product ? (
                  <div style={{ fontSize: "0.9rem" }}>Producto faltante: {t.missing_product}</div>
                ) : null}
                {t.improvement_text ? (
                  <div style={{ fontSize: "0.9rem" }}>Mejora: {t.improvement_text}</div>
                ) : null}
                {t.message ? <div style={{ fontSize: "0.9rem" }}>{t.message}</div> : null}
                {t.charged_card === true || t.charged_card === false ? (
                  <div style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                    ¿Cobró tarjeta?: {t.charged_card ? "Sí" : "No"}
                  </div>
                ) : null}

                {t.status === "open" || t.status === "in_progress" ? (
                  <div style={{ marginTop: "0.5rem", display: "flex", gap: "0.35rem" }}>
                    {t.status === "open" ? (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={busyId === t.id}
                        onClick={() => updateTicket(t.id, "in_progress")}
                      >
                        En curso
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={busyId === t.id}
                      onClick={() => updateTicket(t.id, "resolved")}
                    >
                      Resuelto
                    </button>
                  </div>
                ) : null}

                {t.status === "open" || t.status === "in_progress" ? (
                  <TicketContextPanel
                    ticket={t}
                    busy={busyId === t.id}
                    onRefresh={load}
                  />
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
